import React from 'react';
import { useFeatureFlag, useSubjectLanguageVariants } from '@features/user';
import { Science as LabIcon } from '@mui/icons-material';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks/useModal';

import { useTracking } from '../../../context/TrackingContext';
import { EventType } from '../../../types/events';
import { ToolbarButton } from '../components/ToolbarButton';
import { useToolbarAction } from '../store/hooks';
import { type ToolbarActionProps } from '../types';

export const LabOrderAction: React.FC<ToolbarActionProps> = ({
  context,
  compact,
  presentation,
}) => {
  const { open } = useModal();
  const isEnabled = useFeatureFlag('lab_orders');
  const { badgeCount } = useToolbarAction('order-labs');
  const { singular: subjectLanguage } = useSubjectLanguageVariants();
  const { trackEvent } = useTracking();
  const { toolbar } = useEventTracking();

  if (!isEnabled) return null;

  const handleClick = () => {
    // Track with new Statsig system
    toolbar.actionClicked({
      action_type: 'order_labs',
      has_patient: !!context?.patientId,
    });

    // Keep existing tracking for backward compatibility
    trackEvent(EventType.TOOLBAR_CLICKED, {
      feature: 'lab_order',
      user_agent: navigator.userAgent || null,
      metadata: {
        patient_id: context?.patientId || null,
      },
    });

    open('LAB_ORDER', {
      title: 'Labs',
      patientId: context?.patientId,
      encounterId: context?.encounterId,
      noteId: context?.noteId,
      maxWidth: 'lg',
    });
  };

  return (
    <ToolbarButton
      icon={LabIcon}
      label="Labs"
      tooltip={`Create a lab request form to print or send to your ${subjectLanguage}`}
      onClick={handleClick}
      compact={compact}
      badgeCount={badgeCount}
      dataAttribute="toolbar-order-labs"
      presentation={presentation}
      menuLabel="Send lab"
    />
  );
};
